import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';

import { BonusRewardBody } from 'components/DriverAX/components/BonusReward/BonusRewardBody';
import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import Tooltip from 'components/Tooltip';
import Cell from 'components/V5Gadget/Matrix/Cell';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import resolveDriverRewardOutput from 'components/DriverAX/utils/resolveDriverRewardOutput';
import resolveRewardWithCriteria from 'components/DriverAX/utils/resolveRewardWithCriteria';
import useCurrentAccountId from 'hooks/useCurrentAccountId';
import useValueFormatters from 'hooks/useValueFormatters';

import { QUALIFIES_FOR_BONUS_FIELD } from '../../../constants';
import GridCellErrorBoundary from '../../GridCellErrorBoundary';
import useBonusCellBonusProgram from '../hooks/useBonusCellBonusProgram';
import { QualifiedTooltip } from './BonusQualifiedCell';

const ExpectationsTooltip = ({
  driver,
  bonusPeriodId,
  bonusProgram,
  rewardName,
}: {
  driver: string;
  bonusPeriodId: string;
  bonusProgram: BonusTypes.BonusProgram;
  rewardName: string;
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { selectedSubPeriod } = useContext(BonusPeriodsContext);
  const accountId = useCurrentAccountId();
  const [rewardOutput, setRewardOutput] = useState<
    BonusTypes.Rewards.AnyDriverRewardsOutput | undefined
  >(undefined);

  useEffect(() => {
    let isActive = true;
    resolveDriverRewardOutput({
      api,
      accountId,
      driver,
      bonusProgramId: bonusProgram.id,
      bonusPeriodId,
      subPeriod: selectedSubPeriod,
    }).then((newRewardOutput) => {
      if (isActive && newRewardOutput) {
        setRewardOutput(newRewardOutput);
      }
    });
    return () => {
      isActive = false;
    };
  }, [accountId, api, bonusPeriodId, bonusProgram, driver, selectedSubPeriod]);

  const rewardWithCriteria = useMemo(() => {
    if (!rewardOutput) {
      return undefined;
    }
    return resolveRewardWithCriteria({
      rewards: rewardOutput.rewards,
      criteria: rewardOutput.criteria,
      rewardName,
    });
  }, [rewardName, rewardOutput]);

  if (!rewardOutput || !rewardWithCriteria) {
    return (
      <FlexCentered
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          padding: 16,
          border: '1px solid var(--color-border-secondary)',
          borderRadius: 6,
          minWidth: 300,
        }}
      >
        <Loading />
      </FlexCentered>
    );
  }

  return (
    <div style={{ minWidth: 300 }}>
      <BonusRewardBody
        criteria={rewardWithCriteria.criteria}
        bonusProgram={bonusProgram}
        variant={'tooltip'}
      />
    </div>
  );
};

const BonusRewardCell = ({
  elasticDocument,
  dataset,
  actualField,
  rewardName,
}: {
  elasticDocument: ElasticDocument;
  dataset: string;
  actualField: string;
  rewardName: string;
}) => {
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const bonusProgram = useBonusCellBonusProgram();
  const { formatField } = useValueFormatters();
  const actualValue = useMemo(() => {
    return elasticDocument[actualField];
  }, [actualField, elasticDocument]);

  const isBonusQualified = useMemo(() => {
    return !!elasticDocument[QUALIFIES_FOR_BONUS_FIELD];
  }, [elasticDocument]);

  const actualFormatted = useMemo(() => {
    return formatField({
      field: actualField,
      value: actualValue,
      dataset,
    });
  }, [actualField, actualValue, dataset, formatField]);

  const totalPay = useMemo(() => {
    return elasticDocument['totalPay'];
  }, [elasticDocument]);

  const driver = useMemo(() => {
    return elasticDocument['driver'];
  }, [elasticDocument]);

  const NumberWrapper = useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => {
        if (isBonusQualified && actualValue !== 0) {
          return <Cell rightAlign>{children}</Cell>;
        }

        if (!selectedBonusPeriod || !bonusProgram) {
          return <Cell rightAlign>{children}</Cell>;
        }

        if (!isBonusQualified) {
          return (
            <Cell rightAlign>
              <Tooltip
                isUnstyledTooltip
                content={
                  <QualifiedTooltip
                    driver={driver}
                    bonusProgram={bonusProgram}
                    bonusPeriodId={selectedBonusPeriod.id}
                  />
                }
              >
                {children}
              </Tooltip>
            </Cell>
          );
        }

        if (actualValue === 0) {
          return (
            <Cell rightAlign>
              <Tooltip
                isUnstyledTooltip
                content={
                  <ExpectationsTooltip
                    driver={driver}
                    bonusProgram={bonusProgram}
                    bonusPeriodId={selectedBonusPeriod.id}
                    rewardName={rewardName}
                  />
                }
              >
                {children}
              </Tooltip>
            </Cell>
          );
        }

        return <Cell rightAlign>{children}</Cell>;
      },
    [
      actualValue,
      bonusProgram,
      driver,
      isBonusQualified,
      rewardName,
      selectedBonusPeriod,
    ],
  );

  return (
    <NumberWrapper>
      <span
        style={{
          color: totalPay === 0 ? 'var(--color-grey-5)' : 'var(--color-grey-1)',
        }}
      >
        {actualFormatted}
      </span>
    </NumberWrapper>
  );
};

const BonusRewardCellProvider = (params: ICellRendererParams) => {
  const bonusProgram = useBonusCellBonusProgram();

  const elasticDocument = useMemo(() => {
    return params.node.data as ElasticDocument;
  }, [params]);

  const field = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }

    return colDef.field;
  }, [params]);

  const rewardFields = useMemo(() => {
    const { colDef } = params;
    if (!colDef || !colDef.field) {
      return undefined;
    }
    if (!bonusProgram) {
      return undefined;
    }

    const field = colDef.field;
    const expectationField = bonusProgram.rewardFields.find(
      (f) =>
        f.rewardValueField === field || f.potentialRewardValueField === field,
    );
    if (!expectationField) {
      return undefined;
    }
    return expectationField;
  }, [bonusProgram, params]);

  if (!bonusProgram || !field || !rewardFields) {
    return null;
  }

  const { rewardValueField } = rewardFields;

  return (
    <BonusRewardCell
      elasticDocument={elasticDocument}
      dataset={bonusProgram.dataset}
      actualField={rewardValueField}
      rewardName={rewardFields.name}
    />
  );
};

const Gate = (params: ICellRendererParams) => (
  <GridCellErrorBoundary
    params={params}
    childComponent={<BonusRewardCellProvider {...params} />}
  />
);

export default Gate;
